/**
 * /payroll-l3/cbas — Unions, Locals, and CBAs management.
 *
 * Tabbed master/detail. Lets users create unions/locals/CBAs and drill into
 * a CBA to see its classifications, wage rates, and fringes.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

interface CbasSearch {
  cba?: string;
}

export const Route = createFileRoute("/_auth/payroll-l3/cbas")({
  component: CbasPage,
  validateSearch: (search: Record<string, unknown>): CbasSearch => ({
    cba: typeof search.cba === "string" ? search.cba : undefined,
  }),
});

interface UnionRow {
  id: string;
  code: string;
  name: string;
  abbreviation: string | null;
  is_active: boolean;
}

interface LocalRow {
  id: string;
  union_id: string;
  local_number: string;
  name: string;
  city: string | null;
  state_code: string | null;
  is_active: boolean;
}

interface CBARow {
  id: string;
  entity_id: string;
  local_id: string;
  trade_name: string;
  effective_start: string;
  effective_end: string | null;
  ratified_date: string | null;
  is_active: boolean;
  is_draft: boolean;
}

interface RateExpiryWarning {
  cba_id: string;
  trade_name: string;
  local_id: string;
  classification_id: string;
  classification_code: string;
  classification_name: string;
  warning_type: "wage_rate" | "fringe";
  fringe_type_code: string | null;
  expires_on: string;
  days_remaining: number;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** The CBA parser writes "UNKNOWN" when it can't extract a local number
 *  (e.g. regional councils like WSRCC). Treat that as "no number". */
function isUnknownLocal(n: string | null | undefined): boolean {
  return !n || !n.trim() || n.trim().toUpperCase() === "UNKNOWN";
}

/** Returns the number of days until effective_end (negative = already expired). */
function daysUntilExpiry(effectiveEnd: string | null): number | null {
  if (!effectiveEnd) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(effectiveEnd);
  return Math.ceil((end.getTime() - today.getTime()) / 86_400_000);
}

function CbaStatusChip({ c }: { c: CBARow }) {
  if (c.is_draft) return <Chip label="Draft" size="small" color="warning" />;
  const days = daysUntilExpiry(c.effective_end);
  if (days !== null && days < 0) return <Chip label="Expired" size="small" color="error" />;
  if (days !== null && days <= 90)
    return <Chip label={`Expires in ${days}d`} size="small" color="warning" />;
  if (!c.is_active) return <Chip label="Inactive" size="small" />;
  return <Chip label="Active" size="small" color="success" />;
}

type StatusFilter = "all" | "active" | "expiring" | "expired";

const PAGE_SIZE = 15;

function CbasPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"cbas" | "locals" | "unions">("cbas");
  const [createCbaOpen, setCreateCbaOpen] = useState(false);
  const [createLocalOpen, setCreateLocalOpen] = useState(false);
  const [createUnionOpen, setCreateUnionOpen] = useState(false);
  const [detailCbaId, setDetailCbaId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const routeSearch = Route.useSearch();

  // Deep-link: ?cba=<id> opens that CBA's rate sheet (e.g. from a confirmed contract).
  useEffect(() => {
    if (routeSearch.cba) setDetailCbaId(routeSearch.cba);
  }, [routeSearch.cba]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);

  const unionsQ = useQuery({
    queryKey: ["payroll-l3", "unions"],
    queryFn: () => api<UnionRow[]>("/api/payroll-l3/unions?include_inactive=true"),
  });
  const localsQ = useQuery({
    queryKey: ["payroll-l3", "locals"],
    queryFn: () => api<LocalRow[]>("/api/payroll-l3/locals?include_inactive=true"),
  });
  const cbasQ = useQuery({
    queryKey: ["payroll-l3", "cbas"],
    queryFn: () => api<CBARow[]>("/api/payroll-l3/cbas?include_inactive=true"),
  });
  const expiryQ = useQuery({
    queryKey: ["payroll-l3", "rate-expiry-warnings"],
    queryFn: () => api<RateExpiryWarning[]>("/api/payroll-l3/rate-expiry-warnings?days_ahead=90"),
    staleTime: 5 * 60 * 1000,
  });

  const [unionForm, setUnionForm] = useState({ code: "", name: "", abbreviation: "" });
  const [localForm, setLocalForm] = useState({
    union_id: "",
    local_number: "",
    name: "",
    city: "",
    state_code: "",
  });
  const [cbaForm, setCbaForm] = useState({
    local_id: "",
    trade_name: "",
    effective_start: "",
    effective_end: "",
    ratified_date: "",
  });

  const createUnion = useMutation({
    mutationFn: () =>
      api("/api/payroll-l3/unions", {
        method: "POST",
        body: {
          code: unionForm.code,
          name: unionForm.name,
          abbreviation: unionForm.abbreviation || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "unions"] });
      setCreateUnionOpen(false);
      setUnionForm({ code: "", name: "", abbreviation: "" });
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Failed"),
  });

  const createLocal = useMutation({
    mutationFn: () =>
      api("/api/payroll-l3/locals", {
        method: "POST",
        body: {
          union_id: localForm.union_id,
          local_number: localForm.local_number,
          name: localForm.name,
          city: localForm.city || null,
          state_code: localForm.state_code || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "locals"] });
      setCreateLocalOpen(false);
      setLocalForm({ union_id: "", local_number: "", name: "", city: "", state_code: "" });
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Failed"),
  });

  // entity_id is auto-injected from the current entity context server-side via
  // CurrentEntityId for many endpoints, but CBACreate requires entity_id in
  // the body schema. Fetch the current entity and pass it.
  const meEntities = useQuery({
    queryKey: ["auth", "me", "entities"],
    queryFn: () => api<{ id: string; is_current: boolean }[]>("/api/auth/me/entities"),
  });
  const currentEntityId = meEntities.data?.find((e) => e.is_current)?.id;

  const createCba = useMutation({
    mutationFn: () => {
      if (!currentEntityId) throw new Error("Current entity not loaded");
      return api("/api/payroll-l3/cbas", {
        method: "POST",
        body: {
          entity_id: currentEntityId,
          local_id: cbaForm.local_id,
          trade_name: cbaForm.trade_name,
          effective_start: cbaForm.effective_start,
          effective_end: cbaForm.effective_end || null,
          ratified_date: cbaForm.ratified_date || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "cbas"] });
      setCreateCbaOpen(false);
      setCbaForm({ local_id: "", trade_name: "", effective_start: "", effective_end: "", ratified_date: "" });
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Failed"),
  });

  const localById = (id: string) => localsQ.data?.find((l) => l.id === id);
  const unionById = (id: string) => unionsQ.data?.find((u) => u.id === id);

  const filteredCbas = (cbasQ.data ?? []).filter((c) => {
    if (search) {
      const lc = localById(c.local_id);
      const u = lc ? unionById(lc.union_id) : null;
      const q = search.toLowerCase();
      const hit = [c.trade_name, lc?.name, lc?.local_number, u?.name, u?.abbreviation]
        .some((s) => s?.toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (statusFilter !== "all") {
      const days = daysUntilExpiry(c.effective_end);
      if (statusFilter === "expired") return days !== null && days < 0;
      if (statusFilter === "expiring") return days !== null && days >= 0 && days <= 90;
      if (statusFilter === "active") return !c.is_draft && c.is_active && (days === null || days > 90);
    }
    return true;
  });
  const pagedCbas = filteredCbas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Unions, Locals & CBAs
        </Typography>
        <HelpHint docPath="payroll-l3/cba-setup.md" label="CBA setup help" />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="CBAs" value="cbas" />
        <Tab label="Locals" value="locals" />
        <Tab label="Unions" value="unions" />
      </Tabs>

      {tab === "cbas" && (
        <Box>
              {/* Rate expiry warning — compact, grouped by CBA */}
          {expiryQ.data && expiryQ.data.length > 0 && (() => {
            // Group by cba_id
            const byCba = new Map<string, RateExpiryWarning[]>();
            for (const w of expiryQ.data) {
              if (!byCba.has(w.cba_id)) byCba.set(w.cba_id, []);
              byCba.get(w.cba_id)!.push(w);
            }
            return (
              <Paper variant="outlined" sx={{ mb: 2, borderColor: "warning.main" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <WarningAmberIcon color="warning" fontSize="small" />
                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                    Rates expiring with no successor — upload a new CBA to replace them
                  </Typography>
                </Stack>
                <Box sx={{ px: 2, pb: 1.5 }}>
                  {Array.from(byCba.entries()).map(([cbaId, ws]) => {
                    const minDays = Math.min(...ws.map((w) => w.days_remaining));
                    const wageCount = ws.filter((w) => w.warning_type === "wage_rate").length;
                    const fringeCount = ws.filter((w) => w.warning_type === "fringe").length;
                    const parts = [
                      wageCount > 0 ? `${wageCount} wage rate${wageCount !== 1 ? "s" : ""}` : "",
                      fringeCount > 0 ? `${fringeCount} fringe${fringeCount !== 1 ? "s" : ""}` : "",
                    ].filter(Boolean).join(" + ");
                    return (
                      <Stack key={cbaId} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
                        <Chip
                          label={minDays < 0 ? `Expired ${Math.abs(minDays)}d ago` : `${minDays}d`}
                          size="small"
                          color={minDays < 0 ? "error" : minDays <= 30 ? "error" : "warning"}
                          sx={{ minWidth: 70 }}
                        />
                        <Typography variant="body2">
                          <strong>{ws[0].trade_name}</strong> — {parts} expire {fmtDate(ws[0].expires_on)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ ml: "auto", whiteSpace: "nowrap" }}
                          onClick={() => setDetailCbaId(cbaId)}
                        >
                          View rates
                        </Button>
                      </Stack>
                    );
                  })}
                </Box>
              </Paper>
            );
          })()}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search trade, local, union…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                size="small"
                value={statusFilter}
                exclusive
                onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(0); } }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="expiring">Expiring</ToggleButton>
                <ToggleButton value="expired">Expired</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateCbaOpen(true)}
              disabled={!localsQ.data?.length}
            >
              New CBA
            </Button>
          </Stack>
          {cbasQ.isLoading && <CircularProgress size={24} />}
          {cbasQ.error && <Alert severity="error">Failed to load CBAs.</Alert>}
          {cbasQ.data && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Trade</TableCell>
                    <TableCell>Local</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Ratified</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedCbas.map((c) => {
                    const lc = localById(c.local_id);
                    const u = lc ? unionById(lc.union_id) : null;
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{c.trade_name}</TableCell>
                        <TableCell>
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {u ? `${u.abbreviation ?? u.name} ` : ""}
                          </Box>
                          {lc ? (isUnknownLocal(lc.local_number) ? lc.name : `Local ${lc.local_number} — ${lc.name}`) : c.local_id}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtDate(c.effective_start)} → {c.effective_end ? fmtDate(c.effective_end) : "open"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                          {fmtDate(c.ratified_date)}
                        </TableCell>
                        <TableCell>
                          <CbaStatusChip c={c} />
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setDetailCbaId(c.id)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredCbas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                        {search || statusFilter !== "all"
                          ? "No CBAs match your search or filter."
                          : "No CBAs yet. Create a Union and Local first, then add a CBA."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {filteredCbas.length > PAGE_SIZE && (
                <TablePagination
                  component="div"
                  count={filteredCbas.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                />
              )}
            </>
          )}
        </Box>
      )}

      {tab === "locals" && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateLocalOpen(true)}
              disabled={!unionsQ.data?.length}
            >
              New Local
            </Button>
          </Stack>
          {localsQ.isLoading && <CircularProgress size={24} />}
          {localsQ.data && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Local #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Union</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localsQ.data.map((l) => {
                  const u = unionById(l.union_id);
                  return (
                    <TableRow key={l.id} hover>
                      <TableCell sx={{ fontFamily: "monospace", color: isUnknownLocal(l.local_number) ? "text.disabled" : undefined }}>
                        {isUnknownLocal(l.local_number) ? "—" : l.local_number}
                      </TableCell>
                      <TableCell>{l.name}</TableCell>
                      <TableCell>{u?.name ?? l.union_id}</TableCell>
                      <TableCell>
                        {[l.city, l.state_code].filter(Boolean).join(", ") || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={l.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={l.is_active ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {localsQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      No locals yet. Create a Union, then add Locals to it.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {tab === "unions" && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateUnionOpen(true)}
            >
              New Union
            </Button>
          </Stack>
          {unionsQ.isLoading && <CircularProgress size={24} />}
          {unionsQ.data && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Abbr.</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unionsQ.data.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {u.code}
                    </TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.abbreviation ?? "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={u.is_active ? "success" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {unionsQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      No unions yet. Add an international (e.g. UA, IBEW, IUOE) to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* New Union dialog */}
      <Dialog open={createUnionOpen} onClose={() => setCreateUnionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Union</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Code"
              value={unionForm.code}
              onChange={(e) => setUnionForm((p) => ({ ...p, code: e.target.value }))}
              placeholder="UA, IBEW, IUOE"
              required
            />
            <TextField
              label="Name"
              value={unionForm.name}
              onChange={(e) => setUnionForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <TextField
              label="Abbreviation"
              value={unionForm.abbreviation}
              onChange={(e) => setUnionForm((p) => ({ ...p, abbreviation: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUnionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!unionForm.code || !unionForm.name || createUnion.isPending}
            onClick={() => createUnion.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Local dialog */}
      <Dialog open={createLocalOpen} onClose={() => setCreateLocalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Local</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Union"
              value={localForm.union_id}
              onChange={(e) => setLocalForm((p) => ({ ...p, union_id: e.target.value }))}
              required
            >
              {(unionsQ.data ?? []).map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Local Number"
              value={localForm.local_number}
              onChange={(e) => setLocalForm((p) => ({ ...p, local_number: e.target.value }))}
              required
            />
            <TextField
              label="Name"
              value={localForm.name}
              onChange={(e) => setLocalForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="City"
                value={localForm.city}
                onChange={(e) => setLocalForm((p) => ({ ...p, city: e.target.value }))}
                fullWidth
              />
              <TextField
                label="State"
                value={localForm.state_code}
                onChange={(e) => setLocalForm((p) => ({ ...p, state_code: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 2 }}
                sx={{ width: 100 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLocalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!localForm.union_id || !localForm.local_number || !localForm.name || createLocal.isPending}
            onClick={() => createLocal.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* New CBA dialog */}
      <Dialog open={createCbaOpen} onClose={() => setCreateCbaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New CBA</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Local"
              value={cbaForm.local_id}
              onChange={(e) => setCbaForm((p) => ({ ...p, local_id: e.target.value }))}
              required
            >
              {(localsQ.data ?? []).map((l) => {
                const u = unionById(l.union_id);
                return (
                  <MenuItem key={l.id} value={l.id}>
                    {u?.name ?? "?"} — {isUnknownLocal(l.local_number) ? l.name : `Local ${l.local_number} (${l.name})`}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="Trade Name"
              value={cbaForm.trade_name}
              onChange={(e) => setCbaForm((p) => ({ ...p, trade_name: e.target.value }))}
              placeholder="Inside Wireman, Plumber, Operating Engineer"
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Effective Start"
                type="date"
                value={cbaForm.effective_start}
                onChange={(e) => setCbaForm((p) => ({ ...p, effective_start: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Effective End (expiry)"
                type="date"
                value={cbaForm.effective_end}
                onChange={(e) => setCbaForm((p) => ({ ...p, effective_end: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Ratified / Signed Date"
              type="date"
              value={cbaForm.ratified_date}
              onChange={(e) => setCbaForm((p) => ({ ...p, ratified_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Date the agreement was ratified or signed"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCbaOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!cbaForm.local_id || !cbaForm.trade_name || !cbaForm.effective_start || createCba.isPending}
            onClick={() => createCba.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* CBA detail drawer */}
      <CbaDetailDialog
        cbaId={detailCbaId}
        onClose={() => {
          setDetailCbaId(null);
          if (routeSearch.cba) navigate({ to: "/payroll-l3/cbas", search: {}, replace: true });
        }}
      />
    </Box>
  );
}

interface RateSheetFringe {
  fringe_type_id: string;
  fringe_type_code: string;
  fringe_type_name: string;
  rate: number;
  rate_type: string;
  applies_to: string | null;
  is_employer_paid: boolean;
}

interface RateSheetRow {
  classification_id: string;
  code: string;
  name: string;
  is_apprentice: boolean;
  progression_pct: number | null;
  base_rate: number | null;
  ot_multiplier: number | null;
  dt_multiplier: number | null;
  wage_effective_start: string | null;
  wage_effective_end: string | null;
  fringes: RateSheetFringe[];
  total_package: number | null;
}

interface RetroDetectResult {
  cba_id: string;
  retroactive_from: string;
  total_delta: string;
  affected_employee_count: number;
  pay_run_count: number;
  delta_rows: { fringe_fund_name: string | null; period_start: string; period_end: string; delta_amount: string }[];
  top_impacts: { employee_label: string; delta_amount: string; hours: string }[];
  narrative: string;
  ai: string;
}

function CbaDetailDialog({ cbaId, onClose }: { cbaId: string | null; onClose: () => void }) {
  const open = !!cbaId;
  const [retroDate, setRetroDate] = useState("");
  const [retroResult, setRetroResult] = useState<RetroDetectResult | null>(null);
  const [retroErr, setRetroErr] = useState<string | null>(null);

  const retroMut = useMutation({
    mutationFn: () =>
      api<RetroDetectResult>(`/api/payroll-l3/ai/cbas/${cbaId}/retro-detect`, {
        method: "POST",
        body: { retroactive_from: retroDate },
      }),
    onSuccess: (r) => { setRetroResult(r); setRetroErr(null); },
    onError: (e: unknown) => setRetroErr(e instanceof Error ? e.message : "Retro detect failed"),
  });

  const sheetQ = useQuery({
    queryKey: ["payroll-l3", "cba", cbaId, "rate-sheet"],
    queryFn: () => api<RateSheetRow[]>(`/api/payroll-l3/cbas/${cbaId}/rate-sheet`),
    enabled: open,
  });

  // Derive the ordered set of fringe columns from the data
  const fringeTypes = (() => {
    const seen = new Map<string, { code: string; name: string }>();
    for (const row of sheetQ.data ?? []) {
      for (const f of (row.fringes ?? [])) {
        if (!seen.has(f.fringe_type_code)) {
          seen.set(f.fringe_type_code, { code: f.fringe_type_code, name: f.fringe_type_name });
        }
      }
    }
    return Array.from(seen.values());
  })();

  const fringeByType = (row: RateSheetRow, code: string) =>
    (row.fringes ?? []).find((f) => f.fringe_type_code === code);

  const journeymen = (sheetQ.data ?? []).filter((r) => !r.is_apprentice);
  const apprentices = (sheetQ.data ?? []).filter((r) => r.is_apprentice);

  const colSpan = 4 + fringeTypes.length + 2; // code+name+base+OT/DT + fringes + total + effective

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { maxWidth: "98vw", maxHeight: "90vh" } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>Rate Sheet</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          All wage rates and fringe contributions — as parsed from the CBA
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {/* ── Retro-detect strip ── */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center" flexGrow={1} flexWrap="wrap">
              <AutoAwesomeIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={600}>Detect Retro Impact</Typography>
              <TextField
                type="date"
                size="small"
                label="Retroactive from"
                value={retroDate}
                onChange={(e) => setRetroDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={!retroDate || retroMut.isPending}
                onClick={() => { setRetroResult(null); retroMut.mutate(); }}
                startIcon={retroMut.isPending ? <CircularProgress size={14} /> : undefined}
              >
                Run
              </Button>
            </Stack>
          </Stack>
          {retroErr && <Alert severity="error" sx={{ mt: 1 }}>{retroErr}</Alert>}
          {retroResult && (
            retroResult.total_delta === "0" ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                No retroactive impact — rates already cover this period.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  ${Number(retroResult.total_delta).toFixed(2)} across {retroResult.affected_employee_count} employee{retroResult.affected_employee_count !== 1 ? "s" : ""} · {retroResult.pay_run_count} pay run{retroResult.pay_run_count !== 1 ? "s" : ""}
                </Typography>
                <Typography variant="body2">{retroResult.narrative}</Typography>
              </Alert>
            )
          )}
        </Box>

        {sheetQ.isLoading && (
          <Stack alignItems="center" py={4}><CircularProgress /></Stack>
        )}
        {sheetQ.data && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap", bgcolor: "grey.100" } }}>
                  <TableCell sx={{ minWidth: 130 }}>Code</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Classification</TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>Base Wage</TableCell>
                  <TableCell align="center" sx={{ minWidth: 70 }}>OT</TableCell>
                  {fringeTypes.map((ft) => (
                    <TableCell key={ft.code} align="right" sx={{ minWidth: 80 }}>
                      {ft.code}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ minWidth: 100, bgcolor: "primary.50" }}>
                    Total Pkg
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, color: "text.secondary" }}>Effective</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journeymen.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan}
                      sx={{ bgcolor: "grey.50", py: 0.5, fontWeight: 700, fontSize: "0.7rem",
                            letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}>
                      Journeyman / Foreman
                    </TableCell>
                  </TableRow>
                )}
                {journeymen.map((row) => (
                  <RateSheetDataRow key={row.classification_id} row={row} fringeTypes={fringeTypes} fringeByType={fringeByType} />
                ))}
                {apprentices.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan}
                      sx={{ bgcolor: "grey.50", py: 0.5, fontWeight: 700, fontSize: "0.7rem",
                            letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}>
                      Apprentices
                    </TableCell>
                  </TableRow>
                )}
                {apprentices.map((row) => (
                  <RateSheetDataRow key={row.classification_id} row={row} fringeTypes={fringeTypes} fringeByType={fringeByType} />
                ))}
                {sheetQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      No rate data yet — upload a CBA contract to populate rates.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {sheetQ.data ? `${sheetQ.data.length} classifications · ${fringeTypes.length} fringe type${fringeTypes.length !== 1 ? "s" : ""}` : ""}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function RateSheetDataRow({
  row,
  fringeTypes,
  fringeByType,
}: {
  row: RateSheetRow;
  fringeTypes: { code: string; name: string }[];
  fringeByType: (r: RateSheetRow, code: string) => RateSheetFringe | undefined;
}) {
  const expiringSoon = row.wage_effective_end
    ? (new Date(row.wage_effective_end).getTime() - Date.now()) / 86_400_000 < 90
    : false;

  return (
    <TableRow hover sx={row.is_apprentice ? { opacity: 0.85 } : undefined}>
      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}>
        {row.code}
      </TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <span style={{ fontWeight: row.is_apprentice ? 400 : 600 }}>{row.name}</span>
          {row.is_apprentice && row.progression_pct != null && (
            <Chip label={`${Math.round(row.progression_pct * 100)}%`} size="small" variant="outlined" color="info" />
          )}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {row.base_rate != null ? `$${row.base_rate.toFixed(2)}` : "—"}
      </TableCell>
      <TableCell align="center" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
        {row.ot_multiplier != null ? `${row.ot_multiplier}×` : "—"}
      </TableCell>
      {fringeTypes.map((ft) => {
        const f = fringeByType(row, ft.code);
        const isDeduction = f != null && !f.is_employer_paid;
        return (
          <TableCell key={ft.code} align="right"
            title={isDeduction ? "Employee deduction — not in total package" : undefined}
            sx={{ fontVariantNumeric: "tabular-nums", fontSize: "0.8rem",
                  color: isDeduction ? "text.disabled" : undefined,
                  fontStyle: isDeduction ? "italic" : undefined }}>
            {f ? (f.rate_type === "per_hour" ? `$${f.rate.toFixed(2)}` : `${(f.rate * 100).toFixed(1)}%`) : <span style={{ color: "#bbb" }}>—</span>}
          </TableCell>
        );
      })}
      <TableCell align="right"
        sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums",
              bgcolor: expiringSoon ? "warning.50" : "primary.50",
              color: expiringSoon ? "warning.dark" : "primary.dark" }}>
        {row.total_package != null ? `$${row.total_package.toFixed(2)}` : "—"}
      </TableCell>
      <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
        {row.wage_effective_start ? (
          <span style={expiringSoon ? { color: "#d97706", fontWeight: 600 } : undefined}>
            {fmtDate(row.wage_effective_start)} → {row.wage_effective_end ? fmtDate(row.wage_effective_end) : "open"}
          </span>
        ) : "—"}
      </TableCell>
    </TableRow>
  );
}

